import { Injectable, computed, effect, inject, signal } from '@angular/core';

import { TimezoneMode } from '../enums/timezone-mode.enum';
import { setDisplayTimeZone, standardZoneName } from '../utils/date-format.util';
import { FacilityContextService } from './facility-context.service';
import { StorageService } from './storage.service';

const STORAGE_KEY = 'sx.timezone.mode';

function resolveLocalIanaTimeZoneId(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Tracks the user's chosen display-timezone mode (Facility vs. Local) and resolves it
 * against the currently selected facility. Only the mode is persisted — the effective
 * IANA id/label are always recomputed from the live facility selection, so switching
 * facilities always overrides a prior manual "Local" pick back to that facility's zone.
 */
@Injectable({ providedIn: 'root' })
export class TimezoneService {
  private readonly facilityContext = inject(FacilityContextService);
  private readonly storage = inject(StorageService);

  private readonly modeState = signal<TimezoneMode>(
    this.storage.get<TimezoneMode>(STORAGE_KEY) ?? TimezoneMode.Facility
  );

  readonly mode = this.modeState.asReadonly();

  readonly localIanaTimeZoneId = resolveLocalIanaTimeZoneId();

  readonly facilityIanaTimeZoneId = computed<string | null>(
    () => this.facilityContext.selectedFacility()?.ianaTimeZoneId?.trim() || null
  );

  /** The IANA timezone id currently in effect, resolved from mode + selected facility. */
  readonly effectiveIanaTimeZoneId = computed<string>(() =>
    this.modeState() === TimezoneMode.Facility
      ? this.facilityIanaTimeZoneId() ?? this.localIanaTimeZoneId
      : this.localIanaTimeZoneId
  );

  /** Name for the Local option: the local zone's standard-time name, e.g. "Pakistan Standard Time". */
  readonly localLabel = computed<string>(() => standardZoneName(this.localIanaTimeZoneId));

  /**
   * Name for the Facility option: the facility state's zone id exactly as stored, e.g. "Central
   * Standard Time" for Texas — the same all year, never "Daylight", never an offset. Falls back to the
   * local name when the facility has no usable zone (its times are then shown in local time too).
   */
  readonly facilityLabel = computed<string>(() => {
    if (!this.facilityIanaTimeZoneId()) return this.localLabel();
    return this.facilityContext.selectedFacility()?.timeZoneId?.trim()
      || standardZoneName(this.facilityIanaTimeZoneId()!);
  });

  /** The name of the zone times are currently shown in. */
  readonly effectiveLabel = computed<string>(() =>
    this.modeState() === TimezoneMode.Facility ? this.facilityLabel() : this.localLabel()
  );

  constructor() {
    // Publishes the effective zone (and its name) to the formatting utils so every date follows it.
    effect(() => setDisplayTimeZone(this.effectiveIanaTimeZoneId(), this.effectiveLabel()));
  }

  setMode(mode: TimezoneMode): void {
    this.modeState.set(mode);
    this.storage.set(STORAGE_KEY, mode);
  }
}
